// Meta (Facebook) pixel wrapper. Mirrors `use-matomo.ts`: a co-located global
// augmentation, a module-level dispatch guarded by `import.meta.client`, and thin
// typed `useMetaPixel()` wrappers over Meta's standard conversion events.

declare global {
  interface Window {
    fbq?: (...args: unknown[]) => void;
    _fbq?: unknown;
  }
}

/**
 * Dispatch a `track` call to `fbq`. No-op unless we're on the client, the pixel
 * bootstrap has installed `window.fbq`, and the visitor has not opted out.
 * Trailing `undefined` arguments are omitted so the call shape stays clean —
 * Meta accepts `fbq('track', name)`, `fbq('track', name, params)`, and
 * `fbq('track', name, params, { eventID })`.
 */
function track(
  event: string,
  params?: Record<string, unknown>,
  options?: { eventID?: string },
): void {
  if (!import.meta.client) return;
  if (typeof window.fbq !== 'function') return;
  if (isTrackingOptedOut()) return;

  if (options !== undefined) {
    window.fbq('track', event, params, options);
  } else if (params !== undefined) {
    window.fbq('track', event, params);
  } else {
    window.fbq('track', event);
  }
}

export interface MetaPixelEventOptions {
  eventId: string;
  valueCents?: number | null;
  currency?: string | null;
  eventID?: string;
  contentType?: string;
}

function buildParams(opts: MetaPixelEventOptions): Record<string, unknown> {
  const params: Record<string, unknown> = {
    content_ids: [opts.eventId],
    content_type: 'product',
  };

  // Only send monetary fields when a value is actually present — never emit
  // `value: 0`, which Meta treats as a real zero-dollar conversion.
  if (opts.valueCents != null) {
    params.value = opts.valueCents / 100;
    params.currency = opts.currency || 'USD';
  }

  // Segment commit vs purchase funnels when the caller distinguishes them.
  if (opts.contentType) {
    params.content_category = opts.contentType;
  }

  return params;
}

export function useMetaPixel() {
  function trackViewContent(opts: MetaPixelEventOptions): void {
    track('ViewContent', buildParams(opts), { eventID: opts.eventID });
  }

  function trackInitiateCheckout(opts: MetaPixelEventOptions): void {
    track('InitiateCheckout', buildParams(opts), { eventID: opts.eventID });
  }

  function trackPurchase(opts: MetaPixelEventOptions): void {
    track('Purchase', buildParams(opts), { eventID: opts.eventID });
  }

  return {
    trackViewContent,
    trackInitiateCheckout,
    trackPurchase,
  };
}
